import React, { useState } from 'react';
// Components
import Modal from 'shared/modals/Modal';
import Button from 'shared/buttons';
import DataGrid from 'shared/grids/DataGrid';
import { BaseCell, LinkCell } from 'shared/grids/Cells';
import { BaseDateRangePicker } from 'shared/inputs/datePickers';
import Input from 'shared/inputs/BaseInput';
import { BsArrowRightShort, BsPrinter } from 'react-icons/bs';
// Hooks
import { useToast } from 'shared/toast';
import { useMutation } from '@tanstack/react-query';
// Fetch
import { getDriverDispatches, sendJourneysToDriver } from 'src/fetch/dispatching';
import { getDateStringForRequest, getUtcNow } from 'shared/../helpers/date';
import { format } from 'date-fns';

const DriverNumberInput = (props) => {
  const { value } = props;

  return (
    <Input
      {...props}
      actionComponent={(
        <Button
          icon='hyperlink-open'
          fillMode='flat'
          themeColor='tertiary'
          onClick={() => window.open(`safety/drivers/${value}`, '_blank')}
          disabled={!value || value.trim().length === 0 || value === '00' || value === '000'}
        />
      )}
    />
  );
};

const filterFields = [
  { name: 'driverNumber', label: 'Driver Number', Component: DriverNumberInput },
  { name: 'dateRange', Component: BaseDateRangePicker, className: 'date-range-filter', size: 'large' },
];

const formatSearchParams = ({ dateRange, dateFrom, dateTo, ...others }) => ({
  dateFrom: dateRange?.start ? getDateStringForRequest(dateRange?.start, true) : null,
  dateTo: dateRange?.end ? getDateStringForRequest(dateRange?.end, true) : null,
  dateRange,
  ...others,
});

const initialSearchParams = {
  driverNumber: '',
  dateRange: { start: getUtcNow(), end: getUtcNow() },
};

const SendToDriverCell = (props) => {
  const toast = useToast();

  const { dataItem, isSentToDriver, onSent } = props;
  const { journeyId } = dataItem;

  // Send to driver
  const sendToDriverHandler = useMutation({
    mutationFn: sendJourneysToDriver,
    onSuccess: () => {
      toast.success({ message: 'Sent Successfully' });
      onSent(dataItem);
    },
    onError: (error) => {
      toast.error({ message: 'Failed to send', error });
    },
  });

  const isSending = sendToDriverHandler.isLoading;

  const handleSendToDriver = () => {
    const journeyIds = [journeyId];
    sendToDriverHandler.mutate(journeyIds);
  };

  if (isSentToDriver) {
    return <BaseCell {...props}>Yes</BaseCell>;
  }

  return (
    <BaseCell>
      <Button onClick={handleSendToDriver} isLoading={isSending} disabled={isSending}>
        <BsArrowRightShort />
        Send
      </Button>
    </BaseCell>
  );
};

const getData = ({ dateRange, ...rest }) => getDriverDispatches(rest);

const SearchDriverDispatchesModal = ({ onClose }) => {
  const toast = useToast();

  // Selected journeys
  const [selected, setSelected] = useState([]);

  const onSelectionChange = (newSelected) => {
    const mapped = newSelected.map((item) => ({ ...item, id: item.journeyId }));
    setSelected(mapped);
  };

  // Sent to driver
  const [sentItems, setSentItems] = useState([]);

  const sentJourneysIds = sentItems.map(({ journeyId }) => journeyId);

  const onSent = (dataItem) => setSentItems((prev) => [...prev, dataItem]);

  // Grid data reloading
  const [forceReload, setForceReload] = useState(false);
  const didLoad = () => {
    setForceReload(false);
    setSelected([]);
    setSentItems([]);
  };

  // Send to driver selected items
  const sendToDriverHandler = useMutation({
    mutationFn: sendJourneysToDriver,
    onSuccess: () => {
      toast.success({ message: 'Sent Successfully' });
      setForceReload(true);
    },
    onError: (error) => {
      toast.error({ message: 'Failed to send', error });
    },
  });

  const isSending = sendToDriverHandler.isLoading;

  const handleSendToDriver = () => {
    const journeyIds = selected.filter(({ sentToDriver }) => !sentToDriver).map(({ journeyId }) => journeyId);
    sendToDriverHandler.mutate(journeyIds);
  };

  const allowSendToDriverCount = selected.filter(
    ({ sentToDriver, journeyId }) => !sentToDriver && !sentJourneysIds.includes(journeyId),
  ).length;

  const handlePrint = () => {
    console.log('Print');
  };

  return (
    <Modal isOpen fluid title='Search Driver Dispatches' handleClose={onClose} containerStyle='large'>
      <DataGrid
        name='search-driver-dispatches'
        getData={getData}
        forceReload={forceReload}
        didLoad={didLoad}
        identifierColumn='journeyId'
        filterFields={filterFields}
        formatSearchParams={formatSearchParams}
        initialSearchParams={initialSearchParams}
        allowResetFilters={false}
        hasUnifiedTextSearch={false}
        hideActionsCell
        isMultiSorting={false}
        useSearchQuery={false}
        columns={[
          {
            title: 'Pro #',
            field: 'proNumber',
            cell: ({ dataItem, ...props }) => (
              <LinkCell {...props} to={`/orders/orders/${dataItem.proNumber}`}>
                {dataItem.proNumber}
              </LinkCell>
            ),
            width: 75,
          },
          {
            title: 'Appt. Date',
            field: 'appointmentDate',
            isDate: true,
            width: 100,
          },
          {
            title: 'Arrival Time',
            field: 'arrivalTime',
            cell: ({ dataItem, ...props }) => (
              <BaseCell {...props}>
                {dataItem.arrivalTime ? format(new Date(dataItem.arrivalTime), 'MM/DD/YYYY HH:mm') : null}
              </BaseCell>
            ),
            width: 100,
          },
          { title: 'Dispatcher', field: 'dispatcher', width: 150 },
          { title: 'Container', field: 'containerNumber', width: 125 },
          { title: 'Equipment Type', field: 'equipmentType', width: 75 },
          { title: 'Chassis #', field: 'chassisNumber', width: 150 },
          { title: 'Chassis Type', field: 'chassisType', width: 75 },
          { title: 'Leg Miles', field: 'legMiles', width: 75 },
          { title: 'Origin Type', field: 'originType', width: 100, sortable: false },
          { title: 'Origin Location', field: 'originLocation', width: 150 },
          { title: 'Destination Type', field: 'destinationType', width: 100, sortable: false },
          { title: 'Destination Location', field: 'destinationLocation', width: 150 },
          {
            title: 'Sent to Driver',
            field: 'sentToDriver',
            cell: (props) => (
              <SendToDriverCell
                {...props}
                onSent={onSent}
                isSentToDriver={props.dataItem.sentToDriver || sentJourneysIds.includes(props.dataItem.journeyId)}
              />
            ),
            width: 125,
            sortable: false,
          },
        ]}
        // Select mode
        selectionEnabled
        selected={selected}
        onSelectionChange={onSelectionChange}
        showSelectedCount
        selectedCount={selected.length}
        // Actions
        actionBarContent={
          selected.length > 0 && (
            <>
              {allowSendToDriverCount > 0 && (
                <Button primary onClick={handleSendToDriver} isLoading={isSending} disabled={isSending}>
                  <BsArrowRightShort />
                  {`Send to Driver (${allowSendToDriverCount})`}
                </Button>
              )}
              <Button themeColor='secondary' onClick={handlePrint}>
                <BsPrinter />
                Print
              </Button>
            </>
          )
        }
      />
    </Modal>
  );
};

export default SearchDriverDispatchesModal;
